import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { geocodingAddressStart, geocodingCloseSuggestions, geocodingOpenSuggestions } from '../../../actions/geocoding-address';
import { geocodingAddressSelector } from '../../../selectors/geocoding-address';

/**
 * Hook personnalisé pour gérer l'autocomplete d'adresse avec l'API BAN
 * @param {number} debounceMs - Délai de debounce en millisecondes (défaut: 500)
 * @returns {Object} - { suggestions, loading, searchAddress, closeSuggestions }
 */
const useAddressAutocomplete = function () {
  let debounceMs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 500;
  const dispatch = useDispatch();
  const {
    suggestions,
    suggestionsOpened,
    geocodeAddressLoading
  } = useSelector(geocodingAddressSelector);
  const [inputValue, setInputValue] = useState('');
  const debounceTimeout = useRef(null);

  // Ouvrir automatiquement les suggestions quand elles arrivent
  useEffect(() => {
    if (suggestions && suggestions.features && suggestions.features.length > 0) {
      dispatch(geocodingOpenSuggestions());
    }
  }, [suggestions, dispatch]);

  // Fonction pour rechercher une adresse avec debounce
  const searchAddress = useCallback(value => {
    setInputValue(value);

    // Clear le timeout précédent
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Si la valeur est vide, on ferme les suggestions
    if (!value || value.trim() === '') {
      dispatch(geocodingCloseSuggestions());
      return;
    }

    // Debounce l'appel API
    debounceTimeout.current = setTimeout(() => {
      dispatch(geocodingAddressStart(value));
    }, debounceMs);
  }, [dispatch, debounceMs]);

  // Fonction pour fermer les suggestions
  const closeSuggestions = useCallback(() => {
    dispatch(geocodingCloseSuggestions());
  }, [dispatch]);

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Formater les suggestions pour l'affichage
  const formattedSuggestions = suggestions?.features?.filter(f => f.properties.name !== '' && f.properties.postcode !== '' && f.properties.city !== '').map(f => {
    let name = f.properties.name;
    const housenumber = f.properties.housenumber || '';
    if (housenumber && !name.includes(housenumber)) {
      name = `${housenumber} ${name}`;
    }
    const address = `${name} ${f.properties.postcode} ${f.properties.city}`;
    return {
      address,
      properties: f.properties
    };
  }) || [];
  return {
    suggestions: formattedSuggestions,
    suggestionsOpened,
    loading: geocodeAddressLoading,
    searchAddress,
    closeSuggestions,
    inputValue
  };
};
export default useAddressAutocomplete;